import { migrateWorkspace } from './workspace-migration.js';
/**
 * The workspace value — ADR-015 §3: layout is DATA. One serializable value
 * describes which panels are on stage and how they are arranged: zones
 * (left/right/bottom around the non-negotiable viewport center) holding tab
 * GROUPS of panel placements, one layout PER MODE (the Blender idiom — mode
 * switch swaps the whole arrangement, so no per-panel reconciliation logic
 * exists to get wrong). Pure values, pure transitions, and a LOUD hand-rolled
 * parse (zod stays kernel-only): a persisted layout naming a retired panel is
 * refused with the name in hand, never a silently vanished panel (§7).
 * Panels never see this shape at all — that is the whole point.
 * Depends on: shell-state (ShellMode), panel-ids. DOM-free, store-free.
 * Depended on by: the app store (workspaces slice), workspace-shell, main.
 */
import { isPanelId, REGISTERS, type PanelId, type Register } from './panel-ids.js';
import { SHELL_MODES, type ShellMode } from '../session/shell-state.js';

/** A panel instance in a layout. `register` overrides the workspace default
 *  PER PANEL (ADR-015 Q3, decided) — safe under brief §1.1, because registers
 *  differ in what they show, never in what is true. */
export interface PanelPlacement {
  readonly id: PanelId;
  readonly register?: Register;
}

/** A tab stack — the "group panels" primitive. Exactly one tab is active. */
export interface PanelGroup {
  readonly panels: readonly PanelPlacement[];
  readonly active: PanelId;
  /** Fraction of the zone's axis this group occupies, clamped to [0.1, 0.9]. */
  readonly size: number;
}

export type ZoneName = 'left' | 'right' | 'bottom';
export const ZONE_NAMES: readonly ZoneName[] = ['left', 'right', 'bottom'];

export interface WorkspaceLayout {
  readonly schemaVersion: '2';
  readonly zones: Readonly<Record<ZoneName, readonly PanelGroup[]>>;
  /** The workspace's DEFAULT register; placements may override (§1.1). */
  readonly register: Register;
  /**
   * Zones the user has collapsed to a rail (Allen, 2026-08-01: the shelf
   * must get out of the way when not in use). ABSENT = all expanded — every
   * pre-collapse persisted layout parses unchanged. A collapsed zone keeps
   * its groups in the value (nothing is closed, nothing forgotten); the
   * engine simply renders a rail and mounts no panels.
   */
  readonly collapsed?: readonly ZoneName[];
}

/** One layout per mode — switching modes swaps the whole arrangement. */
export type Workspaces = Readonly<Record<ShellMode, WorkspaceLayout>>;

/** Which modes offer each panel — derived from the registry BY THE CALLER and
 *  injected (ADR-015 §4: the parse never imports the registry). */
export type PanelAvailability = Readonly<Partial<Record<PanelId, readonly ShellMode[]>>>;

/** The shipped per-mode defaults: the timeline strip lives in the bottom zone
 *  wherever it is offered; the character panel lives in the right zone
 *  wherever it is offered — until the command palette lands, a default slot
 *  is the ONLY person-door a panel has (the Hands auto-open being the one
 *  earned exception), and a panel in no default layout is unreachable
 *  (measured: Allen could not find arm-space, 2026-07-31, even incognito);
 *  retarget-review opens clean (its ledger panel is a future registry
 *  entry). */
export function defaultWorkspaces(availability: PanelAvailability): Workspaces {
  const timelineModes = availability['timeline'] ?? [];
  const bodyModes = availability['body'] ?? [];
  const lookModes = availability['look'] ?? [];
  return Object.fromEntries(
    SHELL_MODES.map((mode) => {
      // Materials joins the Character shelf as a tab (join-as-tab shipped
      // 2026-08-01): both are per-avatar property panels, and a second right
      // section would spend width the viewport owns. Zones (ADR-031) is the
      // third tab for the same reason — a zone is a property of the character.
      // Package (ADR-033) is the fourth: what the character becomes in World.
      const rightPanels = [
        ...(bodyModes.includes(mode) ? [{id:'body' as const}] : []),
        ...(lookModes.includes(mode) ? [{id:'look' as const}] : []),
      ];
      const layout: WorkspaceLayout = {
        schemaVersion: '2',
        zones: {
          left: [],
          right:
            rightPanels.length > 0
              ? [{ panels: rightPanels, active: (rightPanels[0] as { id: PanelId }).id, size: 0.2 }]
              : [],
          bottom: timelineModes.includes(mode)
            ? [{ panels: [{ id: 'timeline' }], active: 'timeline', size: 0.28 }]
            : [],
        },
        register: 'creator',
      };
      return [mode, layout];
    }),
  ) as Record<ShellMode, WorkspaceLayout>;
}

/** A refusal that names what it dropped — the caller surfaces it (§7). */
export class WorkspaceParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WorkspaceParseError';
  }
}

function parseLayout(raw: unknown, mode: ShellMode, availability: PanelAvailability): WorkspaceLayout {
  if (typeof raw !== 'object' || raw === null) throw new WorkspaceParseError(`${mode}: layout is not an object`);
  const layout = raw as Record<string, unknown>;
  if (layout['schemaVersion'] !== '2') {
    throw new WorkspaceParseError(`${mode}: unknown workspace schemaVersion ${JSON.stringify(layout['schemaVersion'])}`);
  }
  if (!(REGISTERS as readonly unknown[]).includes(layout['register'])) {
    throw new WorkspaceParseError(`${mode}: unknown register ${JSON.stringify(layout['register'])}`);
  }
  const zonesRaw = layout['zones'];
  if (typeof zonesRaw !== 'object' || zonesRaw === null) throw new WorkspaceParseError(`${mode}: zones missing`);
  const zones = {} as Record<ZoneName, PanelGroup[]>;
  for (const zone of ZONE_NAMES) {
    const groupsRaw = (zonesRaw as Record<string, unknown>)[zone];
    if (!Array.isArray(groupsRaw)) throw new WorkspaceParseError(`${mode}: zone "${zone}" is not an array`);
    zones[zone] = groupsRaw.map((groupRaw: unknown, gi: number) => {
      const where = `${mode}/${zone}[${gi}]`;
      if (typeof groupRaw !== 'object' || groupRaw === null) throw new WorkspaceParseError(`${where}: not a group`);
      const group = groupRaw as Record<string, unknown>;
      if (!Array.isArray(group['panels']) || group['panels'].length === 0) {
        throw new WorkspaceParseError(`${where}: a group holds at least one panel`);
      }
      const panels: PanelPlacement[] = group['panels'].map((placementRaw: unknown) => {
        const placement =
          typeof placementRaw === 'object' && placementRaw !== null ? (placementRaw as Record<string, unknown>) : {};
        const id = placement['id'];
        // The vocabulary clause stays HERE as well as in the shared law, and
        // deliberately: it must fire at THIS placement, before the active-tab
        // and size checks further down, or a retired panel gets reported as a
        // non-member active tab and §7's promise — name what was dropped — is
        // quietly broken. (It was, briefly; the test for that clause caught
        // it.) Availability and uniqueness need the whole layout and belong to
        // assertLayoutOffered below.
        if (typeof id !== 'string' || !isPanelId(id)) {
          throw new WorkspaceParseError(`${where}: saved workspace references panel ${JSON.stringify(id)}, which does not exist`);
        }
        const register = placement['register'];
        if (register !== undefined && !(REGISTERS as readonly unknown[]).includes(register)) {
          throw new WorkspaceParseError(`${where}: panel "${id}" names unknown register ${JSON.stringify(register)}`);
        }
        return register === undefined ? { id } : { id, register: register as Register };
      });
      const ids = panels.map((p) => p.id);
      const active = group['active'];
      if (typeof active !== 'string' || !ids.includes(active as PanelId)) {
        throw new WorkspaceParseError(`${where}: active tab ${JSON.stringify(active)} is not a member of the group`);
      }
      const size = group['size'];
      if (typeof size !== 'number' || !Number.isFinite(size) || size < 0.1 || size > 0.9) {
        throw new WorkspaceParseError(`${where}: size ${JSON.stringify(size)} outside [0.1, 0.9]`);
      }
      return { panels, active: active as PanelId, size };
    });
  }
  const collapsedRaw = layout['collapsed'];
  let collapsed: ZoneName[] | undefined;
  if (collapsedRaw !== undefined) {
    if (!Array.isArray(collapsedRaw)) throw new WorkspaceParseError(`${mode}: collapsed is not an array`);
    const seen = new Set<string>();
    collapsed = collapsedRaw.map((z: unknown) => {
      if (typeof z !== 'string' || !(ZONE_NAMES as readonly string[]).includes(z)) {
        throw new WorkspaceParseError(`${mode}: collapsed names unknown zone ${JSON.stringify(z)}`);
      }
      if (seen.has(z)) throw new WorkspaceParseError(`${mode}: collapsed lists zone "${z}" twice`);
      seen.add(z);
      return z as ZoneName;
    });
  }
  const parsed: WorkspaceLayout = {
    schemaVersion: '2',
    zones,
    register: layout['register'] as Register,
    ...(collapsed === undefined ? {} : { collapsed }),
  };
  assertLayoutOffered(parsed, mode, availability); // the shared law, at the reload door
  return parsed;
}

/** Is this zone collapsed to its rail? Absent field = expanded (the v1 read). */
export function isZoneCollapsed(layout: WorkspaceLayout, zone: ZoneName): boolean {
  return layout.collapsed?.includes(zone) ?? false;
}

/** Collapse a zone to its rail, or expand it. Identity when nothing changes. */
export function setZoneCollapsed(layout: WorkspaceLayout, zone: ZoneName, collapsed: boolean): WorkspaceLayout {
  const now = isZoneCollapsed(layout, zone);
  if (now === collapsed) return layout;
  const next = collapsed
    ? [...(layout.collapsed ?? []), zone]
    : (layout.collapsed ?? []).filter((z) => z !== zone);
  if (next.length === 0) {
    // Empty list = absent field: the value stays in the exact shape every
    // pre-collapse persisted layout already has.
    const rest = { ...layout };
    delete (rest as { collapsed?: readonly ZoneName[] }).collapsed;
    return rest;
  }
  return { ...layout, collapsed: next };
}

/**
 * The MODE-SCOPED law a layout must satisfy: every panel exists in the closed
 * vocabulary, is offered in this mode, and appears once layout-wide.
 *
 * It lives apart from the shape parsing because two doors need it and only one
 * of them reads JSON. The transitions (openPanel, swapPanel) are pure over a
 * layout that does not carry its mode, so they CANNOT enforce availability —
 * which left a hole worth naming (Codex P2, #63): a caller could open a panel
 * into a mode the registry never offered it in, the shell would mount it
 * happily, and only the next boot's parse would object — by rejecting the
 * whole stored value and resetting EVERY workspace preference. A rule enforced
 * at reload but not at write is a rule that punishes the user later for what
 * we accepted now. Applied at both doors, what the shell holds is always
 * something the next boot accepts.
 */
export function assertLayoutOffered(layout: WorkspaceLayout, mode: ShellMode, availability: PanelAvailability): void {
  const seenPanels = new Set<string>();
  for (const zone of ZONE_NAMES) {
    layout.zones[zone].forEach((group, gi) => {
      const where = `${mode}/${zone}[${gi}]`;
      for (const { id } of group.panels) {
        if (!isPanelId(id)) {
          // THE loud clause: a retired/unknown panel is named, never vanished.
          throw new WorkspaceParseError(`${where}: saved workspace references panel ${JSON.stringify(id)}, which does not exist`);
        }
        const offered = availability[id] ?? [];
        if (!offered.includes(mode)) {
          throw new WorkspaceParseError(
            `${where}: panel "${id}" is not offered in mode "${mode}" (offered: ${offered.join(', ') || '(none)'})`,
          );
        }
        if (seenPanels.has(id)) {
          throw new WorkspaceParseError(`${where}: panel "${id}" appears more than once in the layout — one instance per layout`);
        }
        seenPanels.add(id);
      }
    });
  }
}

/** Parse a persisted Workspaces value, loudly. The caller catches
 *  WorkspaceParseError, restores defaults, and SURFACES the message (§7). */
export function parseWorkspaces(raw: unknown, availability: PanelAvailability): Workspaces {
  if (typeof raw !== 'object' || raw === null) throw new WorkspaceParseError('stored workspaces is not an object');
  const record = raw as Record<string, unknown>;
  const out = {} as Record<ShellMode, WorkspaceLayout>;
  for (const mode of SHELL_MODES) {
    if (!(mode in record)) throw new WorkspaceParseError(`stored workspaces has no layout for mode "${mode}"`);
    try { out[mode] = parseLayout(migrateWorkspace(record[mode]), mode, availability); }
    catch(error) { throw new WorkspaceParseError(error instanceof Error ? error.message : String(error)); }
  }
  return out;
}

// ---- Pure transitions. Each returns the SAME layout object when nothing
//      changes (identity = the cheap no-op signal, the shell-state idiom).

function withGroup(
  layout: WorkspaceLayout,
  zone: ZoneName,
  groupIndex: number,
  update: (group: PanelGroup) => PanelGroup | null,
): WorkspaceLayout {
  const groups = layout.zones[zone];
  const group = groups[groupIndex];
  if (group === undefined) {
    throw new WorkspaceParseError(`no group ${zone}[${groupIndex}] — zones hold ${groups.length} group(s)`);
  }
  const next = update(group);
  if (next === group) return layout;
  const nextGroups = next === null ? groups.filter((_, i) => i !== groupIndex) : groups.map((g, i) => (i === groupIndex ? next : g));
  return { ...layout, zones: { ...layout.zones, [zone]: nextGroups } };
}

/** Bring a tab to the front. Refuses a non-member loudly. */
export function activateTab(layout: WorkspaceLayout, zone: ZoneName, groupIndex: number, panel: PanelId): WorkspaceLayout {
  return withGroup(layout, zone, groupIndex, (group) => {
    if (!group.panels.some((p) => p.id === panel)) {
      throw new WorkspaceParseError(`activateTab: "${panel}" is not in ${zone}[${groupIndex}]`);
    }
    return group.active === panel ? group : { ...group, active: panel };
  });
}

/** Swap one panel for another in place (Allen's "swap" primitive). */
export function swapPanel(
  layout: WorkspaceLayout,
  zone: ZoneName,
  groupIndex: number,
  from: PanelId,
  to: PanelPlacement,
): WorkspaceLayout {
  // One instance per LAYOUT: a target already open ANYWHERE (other than as
  // `from` itself — the register-swap case) refuses (Codex P2, #63 — the
  // group-scoped check was the same hole the parse had).
  if (to.id !== from) {
    for (const z of ZONE_NAMES) {
      if (layout.zones[z].some((g) => g.panels.some((p) => p.id === to.id))) {
        throw new WorkspaceParseError(`swapPanel: "${to.id}" is already open in this layout — one instance per layout`);
      }
    }
  }
  return withGroup(layout, zone, groupIndex, (group) => {
    if (!group.panels.some((p) => p.id === from)) {
      throw new WorkspaceParseError(`swapPanel: "${from}" is not in ${zone}[${groupIndex}]`);
    }
    return {
      ...group,
      panels: group.panels.map((p) => (p.id === from ? to : p)),
      active: group.active === from ? to.id : group.active,
    };
  });
}

/** Close a panel; an emptied group dissolves. A missing panel is a no-op —
 *  closing what is not open is not an error a user should meet. Index-free by
 *  construction (Codex P2, #63): the old indexed walk could double-apply a
 *  stale index after a dissolution shifted the list. */
export function closePanel(layout: WorkspaceLayout, panel: PanelId): WorkspaceLayout {
  let changed = false;
  const zones = {} as Record<ZoneName, PanelGroup[]>;
  for (const zone of ZONE_NAMES) {
    zones[zone] = layout.zones[zone].flatMap((group) => {
      if (!group.panels.some((p) => p.id === panel)) return [group];
      changed = true;
      const remaining = group.panels.filter((p) => p.id !== panel);
      if (remaining.length === 0) return []; // dissolve the emptied group
      return [
        {
          ...group,
          panels: remaining,
          active: group.active === panel ? (remaining[0] as PanelPlacement).id : group.active,
        },
      ];
    });
  }
  return changed ? { ...layout, zones } : layout;
}

/** Open a panel into a zone. No-op if already open anywhere — one instance
 *  per layout in v1. A zone that already holds a group receives the panel as
 *  a TAB in its last group, activated (Allen, 2026-08-01: the shelf is ONE
 *  side panel — arrivals join it rather than stacking new shelves beside it);
 *  `size` applies only when the zone was empty and a group must be born.
 *  Opening also EXPANDS a collapsed zone: an arrival the user cannot see —
 *  the Hands auto-open especially — would be an open that silently didn't. */
export function openPanel(layout: WorkspaceLayout, zone: ZoneName, placement: PanelPlacement, size = 0.28): WorkspaceLayout {
  for (const z of ZONE_NAMES) {
    if (layout.zones[z].some((g) => g.panels.some((p) => p.id === placement.id))) return layout;
  }
  const groups = layout.zones[zone];
  const last = groups[groups.length - 1];
  const nextGroups =
    last === undefined
      ? [...groups, { panels: [placement], active: placement.id, size: Math.min(0.9, Math.max(0.1, size)) }]
      : groups.map((g, i) =>
          i === groups.length - 1 ? { ...g, panels: [...g.panels, placement], active: placement.id } : g,
        );
  return setZoneCollapsed({ ...layout, zones: { ...layout.zones, [zone]: nextGroups } }, zone, false);
}

/** Resize a group, clamped to the schema band. */
export function resizeGroup(layout: WorkspaceLayout, zone: ZoneName, groupIndex: number, size: number): WorkspaceLayout {
  const clamped = Math.min(0.9, Math.max(0.1, size));
  return withGroup(layout, zone, groupIndex, (group) => (group.size === clamped ? group : { ...group, size: clamped }));
}
